import { Component, computed, ViewChild, ElementRef, Output, EventEmitter, NgZone, effect, Input, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GenetricDetailComponent } from '@app/common-codes/components/edit-components/generic-detail.component';
import { Register } from '@app/common-codes/decorators/DependencyInjectionRegistreyDecorators';
import { MatwinsSkins } from '@app/common-codes/skins/global.skin';
import { IStaticStylesConfig } from '@app/common-codes/skins/_primitives/IStaticStylesConfig';
import { TranslatePipe } from '@app/common-codes/components/directives/TranslatePipe';
import { GenericValidationService } from '@app/common-codes/services/GenericValidationService';
import { TranslationService } from '@app/common-codes/services/TranslationService';
import { ActivatedRoute, Router } from '@angular/router';
import { OnReadyDirective } from '@app/common-codes/components/directives/OnReadyDirective';
import { FieldContextDirective } from '@app/common-codes/components/directives/FieldContext.directive';
import { AlertModalService } from '@app/common-codes/services/AlertModalService';
import { MatwinsVariablesService } from '@app/common-codes/services/matwinsVariables.service';
import { TruckerActivity } from '@app/common-codes/models/matwins-tenant/TruckerActivity.model';
import { Dock } from '@app/common-codes/models/matwins-tenant/Dock.model';
import { Trucker } from '@app/common-codes/models/matwins-tenant/Trucker.model';
import { FSTREAM_Image } from '@app/common-codes/models/matwins-tenant/FSTREAM_Image.model';
import { LookupService, LookupItem } from '@app/common-codes/services/LookupService';
import { ReactiveBadgeComponent } from '@app/common-codes/components/nested-components/reactive-badge/reactive-badge.component';
import { MultiSelectComponent } from '@app/common-codes/components/nested-components/multi-select/multi-select.component';
import { CurrentTenantService } from '@app/common-codes/services/CurrentTenantService';
import { FieldWrapperComponent } from '@app/common-codes/components/nested-components/field-wrapper/field-wrapper.component';
import { DownloadDocumentComponent } from '@app/common-codes/components/nested-components/download-documents/download-document.component';
import { DateTimePickerComponent } from '@app/common-codes/components/nested-components/date-time-picker/date-time-picker.component';


@Register(TruckerActivity)
@Component({
  selector: 'app-trucker-activity-detail',
  templateUrl: './trucker-activity-detail.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, FormsModule, TranslatePipe, OnReadyDirective, ReactiveBadgeComponent, MultiSelectComponent, FieldWrapperComponent, DownloadDocumentComponent, DateTimePickerComponent, FieldContextDirective]
})
export class TruckerActivityDetailComponent extends GenetricDetailComponent<TruckerActivity> {
  // @Input() override EntityInstance: TruckerActivity | null = null;
  @Input() fkPointOfServiceId: number | null = null;
  override ComponentEntityClass = TruckerActivity;
  Skin = MatwinsSkins.V1 as IStaticStylesConfig;
  @Output() itemSaved = new EventEmitter<TruckerActivity>();
  // @ViewChild('fileInput') declare fileInput: ElementRef<HTMLInputElement>;


  // activityStatusLookup = computed(() => this.getLookupData('ActivityStatus'));

  activityStatusLookup = signal<LookupItem[]>([]);
  activityDeliveryTypeLookup = signal<LookupItem[]>([]);
  pointOfServiceLookup = signal<LookupItem[]>([]);
  truckerLookup = signal<LookupItem[]>([]);
  dockLookup = signal<LookupItem[]>([]);

  filteredTruckerLookup = computed(() => {
    const truckerLookup = this.truckerLookup();
    const entity = this.editingEntity();
    const fkPointOfServiceId = entity?.fK_PointOfService;
    if (!truckerLookup || !fkPointOfServiceId || !entity) {
      return truckerLookup;
    }
    return truckerLookup.filter(trucker => trucker.additionalData['fk_pos'] === fkPointOfServiceId);
  });

  filteredDockLookup = computed(() => {
    const dockLookup = this.dockLookup();
    const entity = this.editingEntity();
    const fkPointOfServiceId = entity?.fK_PointOfService;
    if (!dockLookup || !fkPointOfServiceId || !entity) {
      return dockLookup;
    }
    return dockLookup.filter(dock => dock.additionalData['fk_pointofservice'] === fkPointOfServiceId);
  });

  isPosSelected = computed(() => !!this.editingEntity()?.selectedPK_Pos?.length);


  onStatusChange(newStatus: number): void {
    const currentEntity = this.editingEntity();
    if (currentEntity) {
      const newEntity = Object.assign(new TruckerActivity(), currentEntity);
      newEntity.status = newStatus;
      this.editingEntity.set(newEntity);
    }
  }

  onDeliveryTypeChange(newValue: number): void {
    const currentEntity = this.editingEntity();
    if (currentEntity) {
      const newEntity = Object.assign(new TruckerActivity(), currentEntity);
      newEntity.deliveryType = newValue;
      this.editingEntity.set(newEntity);
    }
  }

  constructor(
    route: ActivatedRoute,
    router: Router,
    translationService: TranslationService,
    validationService: GenericValidationService,
    alertModalService: AlertModalService,
    matwinsVariables: MatwinsVariablesService,
    public currentTenantService: CurrentTenantService,
    ngZone: NgZone
  ) {
    super(route, router, translationService, validationService, ngZone);
    this.alertModalService = alertModalService;

    effect(() => {
      const entity = this.editingEntity();
      if (entity) {
        // Lire la revision : si les listes arrivent en retard, l'effet repasse et les remplit.
        this.lookupService.revision();
        this.activityStatusLookup.set(this.lookupService.getLookupData('ActivityStatus'));
        const activityDeliveryTypeLookup = this.lookupService.getLookupData('ActivityDeliveryType');
        this.activityDeliveryTypeLookup.set(activityDeliveryTypeLookup);
        this.pointOfServiceLookup.set(this.getLookupData('PointOfService'));
        this.truckerLookup.set(this.getLookupData('Trucker'));
        this.dockLookup.set(this.getLookupData('Dock'));
        this.loadPos();
        this.loadTrucker();
        // this.imageUrl = 'Documents/' + entity.imagePhoto?.rawData_Path || '';

      }
    });

  }


  loadPos() {
    const entity = this.editingEntity();
    if (!entity) {
      return;
    }
    const selected = entity.fK_PointOfService;
    if (!selected) {
      return;
    }
    entity.selectedPK_Pos = [selected.toString()];
  }

  loadTrucker() {
    const entity = this.editingEntity();
    if (!entity) {
      return;
    }
    const selected = entity.fK_Trucker;
    if (!selected) {
      return;
    }
    entity.selectedPK_Trucker = [selected.toString()];
  }

  override getEmptyEntity(): TruckerActivity {
    const truckerActivity = new TruckerActivity();
    truckerActivity.startDate = new Date();
    truckerActivity.status = 1;
    truckerActivity.deliveryType = 1;
    truckerActivity.dock = null; // new Dock();
    truckerActivity.fK_Dock = null;
    truckerActivity.trucker = null;
    truckerActivity.imagePhoto = null;
    truckerActivity.imageAgreements = null; // new FSTREAM_Image();
    truckerActivity.imageSignature = null; // new FSTREAM_Image();
    if (this.fkPointOfServiceId) {
      truckerActivity.fK_PointOfService = this.fkPointOfServiceId;
    }
    else if (this.matwinsVariablesService.posId) {
      truckerActivity.fK_PointOfService = this.matwinsVariablesService.posId ?? 0;
    }
    return truckerActivity;
  }

  override parseToEntityInstance(): void {
    if (this.EntityInstance && this.editingEntity()) {
      Object.assign(this.EntityInstance, this.editingEntity());
    }
  }

  performBeforeSave(entity: any) {
    if (entity) {
      const tenant = this.currentTenantService.currentTenant();
      const pos = tenant?.pointOfServices?.find((p: any) => p.id === entity.fK_PointOfService);
      entity.responsibles = pos?.truckerResponsibleAccounts ?? [];

      //Message pour le trucker dans sa langue
      const languages = this.getLookupData('i18nLanguage') as LookupItem[];
      const currentLanguageId = languages.find(l => l.value === this.translationService.currentLanguage)?.key;
      let truckerLanguageId = '';
      if (entity.trucker?.fK_Language != currentLanguageId)
        truckerLanguageId = `_${languages.find(l => l.value === this.translationService.currentLanguage)?.value}` || '';
      entity.messages = {};
      entity.messages['truckerDockEmailBody'] = this.translationService.instant(`messages.multi-langual-messages.trucker-dock.email-body${truckerLanguageId}`);
      entity.messages['truckerDockEmailSubject'] = this.translationService.instant(`messages.multi-langual-messages.trucker-dock.email-subject${truckerLanguageId}`);
      entity.messages['truckerDockSmsBody'] = this.translationService.instant(`messages.multi-langual-messages.trucker-dock.sms-body${truckerLanguageId}`);
      entity.messages['truckerInsideEmailBody'] = this.translationService.instant(`messages.multi-langual-messages.trucker-inside.email-body${truckerLanguageId}`);
      entity.messages['truckerInsideEmailSubject'] = this.translationService.instant(`messages.multi-langual-messages.trucker-inside.email-subject${truckerLanguageId}`);
      entity.messages['truckerInsideSmsBody'] = this.translationService.instant(`messages.multi-langual-messages.trucker-inside.sms-body${truckerLanguageId}`);
      entity.messages['truckerResponsiblesCheckinEmailBody'] = this.translationService.instant(`messages.multi-langual-messages.trucker-responsibles-checkin.email-body${truckerLanguageId}`);
      entity.messages['truckerResponsiblesCheckinEmailSubject'] = this.translationService.instant(`messages.multi-langual-messages.trucker-responsibles-checkin.email-subject${truckerLanguageId}`);
      entity.messages['truckerResponsiblesCheckinSmsBody'] = this.translationService.instant(`messages.multi-langual-messages.trucker-responsibles-checkin.sms-body${truckerLanguageId}`);
      entity.messages['truckerResponsiblesCheckinTeamsBody'] = this.translationService.instant(`messages.multi-langual-messages.trucker-responsibles-checkin.teams-body${truckerLanguageId}`);

      //mettre l'url courante + /qrcode/:tenant/:pos/uploads — l'id de l'activité est ajouté côté serveur
      entity.qrCodeUploadUrl = `${window.location.origin}/qrcode/${tenant?.managerClient}/${entity.fK_PointOfService}/uploads`;
      //SignOutUrl
      entity.signoutUrl = `${window.location.origin}/qrcode/${tenant?.managerClient}/${entity.fK_PointOfService}/trucker-checkout`;

      entity.trucker = null;
      entity.dock = null;


    }
  }

  async saveItem(): Promise<void> {
    const entity = this.editingEntity() as any;

    this.performBeforeSave(entity);
    this.editingEntity.set(entity);

    const savedItem = await this.save();
    if (savedItem) {
      this.performSavedLookup(savedItem);
      this.itemSaved.emit(savedItem);
    }
  }


  onPOSselectChange(evt: string[]) {

    const truckerActivity = this.editingEntity();
    if (!truckerActivity) {
      return;
    }

    const selectedPosIds = (evt ?? [])
      .map(k => Number(k))
      .filter(n => Number.isFinite(n));

    truckerActivity.fK_PointOfService = selectedPosIds[0];
    truckerActivity.selectedPK_Pos = evt;

    if (truckerActivity.fK_Trucker) {
      truckerActivity.fK_Trucker = undefined;
      truckerActivity.selectedPK_Trucker = [];
      truckerActivity.trucker = null;
    }
    truckerActivity.fK_Dock = null;
    truckerActivity.dock = null;

  }

  onTruckerselectChange(evt: string[]) {
    const truckerActivity = this.editingEntity();
    if (!truckerActivity) {
      return;
    }

    const selectedTruckerIds = (evt ?? [])
      .map(k => Number(k))
      .filter(n => Number.isFinite(n));

    truckerActivity.fK_Trucker = selectedTruckerIds[0];
    truckerActivity.selectedPK_Trucker = evt;


  }

  performSavedLookup(savedItem: TruckerActivity) {
    const truckers = this.lookupService.getLookupData('Trucker');
    const trucker = truckers.find(t => t.id === savedItem.fK_Trucker?.toString());
    if (trucker) {
      savedItem.trucker = new Trucker();
      savedItem.trucker.id = Number(trucker.id);
      savedItem.trucker.fullName = trucker.additionalData ? trucker.additionalData['fullname'] : '';
      savedItem.trucker.companyName = trucker.additionalData ? trucker.additionalData['companyname'] : '';
    }

    if (savedItem.fK_Dock) {
      const docks = this.filteredDockLookup();
      const currentDock = docks.find(t => t.id === savedItem.fK_Dock?.toString());
      if (currentDock) {
        savedItem.dock = new Dock();
        savedItem.dock.id = Number(currentDock.id);
        savedItem.dock.iD_SP = currentDock.value;
      }
    }
  }


  async saveAndCloseItem(): Promise<void> {
    const entity = this.editingEntity() as any;

    this.performBeforeSave(entity);
    this.editingEntity.set(entity);
    const savedItem = await this.save();
    if (savedItem) {

      this.performSavedLookup(savedItem);
      this.itemSaved.emit(savedItem);
      this.closeModal.emit();
    }
  }


  onDateTimePropretyChange(date: Date | null, propretyName: 'startDate' |'endDate'): void {
    const entity = this.editingEntity();
    if (entity) {
      entity[propretyName]= date ?? undefined;
      const newEntity = Object.assign(Object.create(Object.getPrototypeOf(entity)), entity);
      this.editingEntity.set(newEntity);
    }
  }
}

